import { Trait } from './traits/trait';
import { shuffledIndexes } from './traits/shuffleTraits';
import outcomeTable, { OutcomeTable } from './outcomeTable';
import PRNG from './PRNG';
import traits from './traits/traits.json';
import eventCrucibles from './traits/eventCrucibles.json';
import colors from './traits/colors.json';

type TraitsData = {
  [key: string]: Trait[];
};

type EventCrucibleData = {
  [key: string]: any;
};

export default class CrucibleMetadata {
  public crucibleId: string;
  public prng: PRNG;
  public centralPiece: Trait;
  public clock: Trait;
  public planetaryClock: Trait;
  public gyroscope: Trait;
  public runes: Trait;
  public rewardProgram: Trait;
  public gems: Trait;
  public frame: Trait;
  public cogwheels: Trait;
  public multiplier: Trait;
  public shellOuterRing: Trait;
  public shellOrnamentRing: Trait;
  public shellInnerRing: Trait;
  public shellGlass: Trait;
  public shellCutShape: Trait;
  public magicCircle: Trait;
  public color: string;

  public mintTimestamp: number;
  public mintBlockNumber: number;

  public coinStyle: string;

  constructor(
    crucibleId: string,
    mintBlockNumber: number,
    mintTimestamp: number,
    chainId: number
  ) {
    this.crucibleId = crucibleId;
    this.prng = new PRNG(this.crucibleId, mintBlockNumber, mintTimestamp);
    this.mintTimestamp = mintTimestamp;
    this.mintBlockNumber = mintBlockNumber;

    const specialTraitsData: { [key: string]: { [key: string]: any } } =
      eventCrucibles;

    if (this.crucibleId in specialTraitsData) {
      const specialTraits = specialTraitsData[this.crucibleId];

      this.clock = this._pickEventCrucibleTrait('clock', specialTraits);
      this.centralPiece = this._pickEventCrucibleTrait(
        'centralPiece',
        specialTraits
      );
      this.planetaryClock = this._pickEventCrucibleTrait(
        'planetaryClock',
        specialTraits
      );
      this.gyroscope = this._pickEventCrucibleTrait('gyroscope', specialTraits);
      this.runes = this._pickEventCrucibleTrait('runes', specialTraits);
      this.rewardProgram = this._pickEventCrucibleTrait(
        'rewardProgram',
        specialTraits
      );
      this.frame = this._pickEventCrucibleTrait('frame', specialTraits);
      this.gems = this._pickEventCrucibleTrait('gems', specialTraits);
      this.cogwheels = this._pickTrait('cogwheels'); // there's only one cogwheels, TODO: remove it from crucibleMetadata
      this.multiplier = this._pickEventCrucibleTrait(
        'multiplier',
        specialTraits
      );
      this.shellOuterRing = this._pickEventCrucibleTrait(
        'shell',
        specialTraits
      );
      this.shellOrnamentRing = this._pickEventCrucibleTrait(
        'shell',
        specialTraits
      );
      this.shellInnerRing = this._pickEventCrucibleTrait(
        'shell',
        specialTraits
      );
      this.shellGlass = this._pickEventCrucibleTrait('glass', specialTraits);
      this.shellCutShape = this._pickEventCrucibleTrait('star', specialTraits);
      this.magicCircle = this._pickEventCrucibleTrait(
        'magicCircle',
        specialTraits
      );

      this.coinStyle = specialTraits.coinStyle ? specialTraits.coinStyle : '';

      if (
        !specialTraits.color &&
        !specialTraits.centralPiece &&
        this.centralPiece !== null
      ) {
        this.color = colors[traits.centralPiece.indexOf(this.centralPiece)];
      } else {
        this.color = specialTraits.color;
      }

      return;
    }

    this.clock = this._pickTrait('clock');
    this.centralPiece = this._pickTrait('centralPiece');
    this.planetaryClock = this._pickTrait('planetaryClock');
    this.gyroscope = this._pickTrait('gyroscope');
    this.runes = this._pickTrait('runes');
    this.rewardProgram = this._pickTrait('rewardProgram');
    this.gems = this._pickTrait('gems');
    this.frame = this._pickTrait('frame');
    this.cogwheels = this._pickTrait('cogwheels'); // there's only one cogwheels, TODO: remove it from crucibleMetadata
    this.multiplier = this._pickTrait('multiplier');
    this.shellOuterRing = this._pickTrait('shell');
    this.shellOrnamentRing = this._pickTrait('shell');
    this.shellInnerRing = this._pickTrait('shell');
    this.shellGlass = this._pickTrait('glass');
    this.shellCutShape = this._pickTrait('star');
    this.magicCircle = this._pickTrait('magicCircle');
    this.color = colors[traits.centralPiece.indexOf(this.centralPiece)];
    this.coinStyle = ''; // Coin style for Crucibles is empty, and a string with the style name for event crucibles
  }

  _pickTrait(cruciblePiece: string): Trait {
    const shuffledTraits: OutcomeTable = this._shuffleTraits(cruciblePiece);
    const weight: number = this.prng.randomInt('crucibleTraits', 10000);
    const trait = outcomeTable.pickOutcome(shuffledTraits, weight);
    return trait;
  }

  _shuffleTraits(piece: string): OutcomeTable {
    let shuffledTraits: OutcomeTable = [];
    let traitsData: TraitsData = traits;
    let data = traitsData[piece];

    for (let i = 0; i < data.length; i++) {
      shuffledTraits[i] = data[shuffledIndexes[piece][i]];
    }

    return shuffledTraits;
  }

  _pickEventCrucibleTrait(
    cruciblePiece: string,
    specialCrucibleData: EventCrucibleData
  ) {
    if (specialCrucibleData[cruciblePiece]) {
      return specialCrucibleData[cruciblePiece];
    } else {
      return this._pickTrait(cruciblePiece);
    }
  }
}
